import type { Routine } from '@sb/remote-control/types';
import { listAllSteps } from '@sb/remote-control/util/listAllSteps';
import { isStepEffectivelySuppressed } from '@sb/remote-control/util/stepSuppression';
import { callSubroutineTargetId } from '@sb/remote-control/util/subroutines/callSubroutineTargetId';

import type { ResolvedSubroutine, ValidationContext } from '../types';

import { runValidation } from './runValidation';

type SubroutineCall = {
  /** Routine holding the `CallSubroutine` step. */
  callerID: string;
  /** Routine the step calls. */
  targetID: string;
  /** The call step will not run, so neither will anything it reaches. */
  suppressed: boolean;
};

/**
 * Every subroutine-to-subroutine call in the resolved graph, flattened into
 * edges once so both closes below iterate the same list instead of re-walking
 * the trees.
 *
 * Two steps calling the same subroutine stay two edges. They can differ in
 * suppression, and collapsing them could keep the suppressed one and lose a
 * weld that a live call reaches.
 */
function listSubroutineCalls(
  subroutines: ReadonlyMap<string, Routine.ConvertedResponse>,
): SubroutineCall[] {
  const calls: SubroutineCall[] = [];

  for (const [callerID, sub] of subroutines) {
    for (const step of listAllSteps(sub.steps)) {
      if (step.stepKind !== 'CallSubroutine') continue;

      const targetID = callSubroutineTargetId(
        sub.stepConfigurations[step.id]?.args,
      );

      if (!targetID) continue;

      calls.push({
        callerID,
        targetID,
        suppressed: isStepEffectivelySuppressed(step, sub.stepConfigurations),
      });
    }
  }

  return calls;
}

/**
 * Whether the routine's own steps weld. A suppressed Weld step does not run,
 * so it does not make the routine one that welds.
 */
function ownStepsWeld(sub: Routine.ConvertedResponse): boolean {
  return listAllSteps(sub.steps).some(
    (step) =>
      step.stepKind === 'Weld' &&
      !isStepEffectivelySuppressed(step, sub.stepConfigurations),
  );
}

/**
 * Spreads a fact from callees to their callers until it stops spreading.
 * Monotonic — a caller's entry is never overwritten, so a routine keeps the
 * fact it holds in its own right and this terminates.
 *
 * A target with no entry carries nothing, which is also how a target outside
 * the resolved subgraph is handled: a deleted subroutine is reported by
 * `CallSubroutineRule.checkRoutineExists` against `knownRoutineIds`, and must
 * not be inferred as anything here.
 */
function propagateToCallers<T>(
  fact: Map<string, T>,
  calls: readonly SubroutineCall[],
  edgeCarriesFact: (call: SubroutineCall) => boolean,
): void {
  let changed = true;

  while (changed) {
    changed = false;

    for (const call of calls) {
      if (fact.has(call.callerID) || !edgeCarriesFact(call)) continue;

      const inherited = fact.get(call.targetID);

      if (inherited === undefined) continue;

      fact.set(call.callerID, inherited);
      changed = true;
    }
  }
}

/**
 * Per-subroutine facts for the resolved subroutine graph. Returns the Map
 * directly — rules read it via
 * `RuleEvaluationContext.resolvedSubroutines.get(id)`.
 *
 * Both facts are seeded per routine and then spread to callers by the same
 * close. The isolation pass behind `isValid` calls `runValidation` directly
 * rather than `precheckForExecution`, so recursion cannot re-enter the
 * cascade — termination is structural, not dependent on caller convention.
 *
 * The two differ only in which calls carry them. Invalidity travels through
 * every call, because a broken subroutine is broken whether or not this run
 * enters it. A weld travels only through calls a run would make.
 */
export function computeResolvedSubroutines(
  subroutines: ReadonlyMap<string, Routine.ConvertedResponse>,
  baseCtx: ValidationContext,
): ReadonlyMap<string, ResolvedSubroutine> {
  // Strip parent-specific fields from the inner ctx. `subroutines` and
  // `subroutineGraphErrors` describe the parent's call graph, not the
  // subroutine's — leaving them in would cause the routine-level
  // graph-errors rule to fire for every subroutine and falsely mark them
  // all invalid. Workspace-global fields (knownRoutineIds, equipment,
  // globalSpace, etc.) propagate.
  const innerCtx: ValidationContext = {
    ...baseCtx,
    subroutines: undefined,
    subroutineGraphErrors: undefined,
  };

  // Presence means invalid, so absence needs no sentinel and cannot be
  // confused with "not in the resolved subgraph".
  const invalid = new Map<string, true>();

  // Keyed by every routine that reaches a weld, valued with the name of the
  // routine that actually holds it — the only one an operator can act on.
  const weldSource = new Map<
    string,
    NonNullable<ResolvedSubroutine['weldSource']>
  >();

  for (const [id, sub] of subroutines) {
    if (!runValidation(sub, innerCtx).ok) invalid.set(id, true);

    if (ownStepsWeld(sub)) weldSource.set(id, sub.name);
  }

  const calls = listSubroutineCalls(subroutines);

  propagateToCallers(invalid, calls, () => true);

  // A suppressed CallSubroutine step takes its whole callee subtree with it.
  propagateToCallers(weldSource, calls, (call) => !call.suppressed);

  const out = new Map<string, ResolvedSubroutine>();

  for (const [id, sub] of subroutines) {
    out.set(id, {
      name: sub.name,
      isValid: !invalid.has(id),
      weldSource: weldSource.get(id),
    });
  }

  return out;
}
